import React, { useEffect, useState } from 'react';
import { useAuth } from '../AuthProvider';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, CircularProgress, Button, TextField, Stack, Alert, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import { baseUrl_SPB } from '../BaseUrl';
import { Drawer } from 'antd';
import { v4 as uuidv4 } from 'uuid';

const CopyButton = ({ value }) => {
  const [copied, setCopied] = React.useState(false);
  if (!value) return null;
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'}>
      <IconButton size="small" onClick={handleCopy} sx={{ ml: 0.5, padding: '2px' }}>
        <ContentCopyIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  );
};

const InventoryManagement = () => {
  const authContext = useAuth();
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editError, setEditError] = useState(null);
  const [editSuccess, setEditSuccess] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchInventory = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${baseUrl_SPB}/api/v1/inventory/getinventories`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + authContext.token,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch inventory');
      let data = await response.json();
      // Add unique key for each row
      data = data.map(item => ({ ...item, key: item.id || uuidv4() }));
      setInventory(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if(authContext?.token)
      fetchInventory();
    // eslint-disable-next-line
  }, [authContext.token]);

  // Open edit drawer
  const handleEdit = (item) => {
    // Ensure material is fallback-initialized if missing in back-end item
    setEditItem({ isMaterial: 'Y', ...item });
    setEditError(null);
    setEditSuccess(null);
    setEditOpen(true);
  };

  // Handle edit form change
  const handleEditChange = (e) => {
    setEditItem({ ...editItem, [e.target.name]: e.target.value });
  };

  // Handler for custom checkbox inside edit drawer
  const handleCheckboxChange = (e) => {
    setEditItem({ ...editItem, [e.target.name]: e.target.checked ? 'Y' : 'N' });
  };

  // Validate edit form with conditional rules
  const validateEdit = () => {
    if (!editItem.itemName) return 'Item name is mandatory';
    if (!editItem.itemDescription) return 'Item description is mandatory';
    if (!editItem.itemCategory) return 'Item category is mandatory';
    if (!editItem.itemNumber) return 'Item number is mandatory';
    if (!editItem.price || isNaN(editItem.price)) return 'Price is mandatory and must be a valid number';

    // Core validation only run if item is a Material
    if (editItem.isMaterial === 'Y') {
      if (!editItem.sku) return 'SKU is mandatory';
      if (!editItem.barcode) return 'Barcode is mandatory';
      if (!editItem.qrCode) return 'QR code is mandatory';
      if (!editItem.binCode) return 'Bin code is mandatory';
      
      const binCodeRegex = /^[a-zA-Z0-9\-]{3,20}$/;
      if (!binCodeRegex.test(editItem.binCode)) return 'Bin code must be 3-20 characters, alphanumeric or hyphen only';
      
      if (!editItem.quantity || isNaN(editItem.quantity)) return 'Quantity is mandatory and must be a valid number';
      if (!editItem.supplierName) return 'Supplier name is mandatory';
    }

    return null;
  };

  // Save edit
  const handleEditSave = async () => {
    setEditError(null);
    setEditSuccess(null);
    const errMsg = validateEdit();
    if (errMsg) {
      setEditError(errMsg);
      return;
    }
    setEditLoading(true);
    try {
      const response = await fetch(`${baseUrl_SPB}/api/v1/inventory/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + authContext.token,
        },
        body: JSON.stringify(editItem),
      });
      if (!response.ok) throw new Error('Failed to update inventory');
      setEditSuccess('Inventory item updated successfully!');
      setEditOpen(false);
      fetchInventory();
    } catch (err) {
      setEditError(err.message);
    } finally {
      setEditLoading(false);
    }
  };

  // Check if user can delete
  const canDelete = authContext?.role === 'ROLE_MANAGER' || authContext?.role === 'ROLE_CMANAGER';

  // Handle delete
  const handleDelete = (item) => {
    setDeleteItem(item);
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleDeleteConfirm = async () => {
    if (!deleteItem) return;
    setDeleteLoading(true);
    try {
      const response = await fetch(`${baseUrl_SPB}/api/v1/inventory/delete?id=${deleteItem.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + authContext.token,
        },
      });
      if (!response.ok) throw new Error('Failed to delete inventory item');
      setDeleteDialogOpen(false);
      setDeleteItem(null);
      fetchInventory();
    } catch (err) {
      alert(err.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Paper sx={{ padding: 2 }}>
      <Typography variant="h5" gutterBottom>Inventory Management</Typography>
      {loading ? <CircularProgress /> : error ? <Typography color="error">{error}</Typography> : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <Typography component="th" sx={{ display: 'table-cell', fontWeight: 'bold', padding: '6px 16px' }}>Name</Typography>
                <TableCell>Category</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Bin</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>Edit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inventory.map(item => (
                <TableRow key={item.key}>
                  <TableCell>{item.itemName}<CopyButton value={item.itemName} /></TableCell>
                  <TableCell>{item.itemCategory}</TableCell>
                  {/* Graceful placeholders if field is labor type ('N') */}
                  <TableCell>{item.isMaterial === 'N' ? '—' : <>{item.sku}<CopyButton value={item.sku} /></>}</TableCell>
                  <TableCell>{item.isMaterial === 'N' ? '—' : <>{item.binCode}<CopyButton value={item.binCode} /></>}</TableCell>
                  <TableCell>{item.isMaterial === 'N' ? '—' : item.quantity}</TableCell>
                  <TableCell>{item.isMaterial === 'N' ? '—' : <>{item.supplierName}<CopyButton value={item.supplierName} /></>}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Button variant="outlined" size="small" onClick={() => handleEdit(item)}>Edit</Button>
                      {canDelete && (
                        <Tooltip title="Delete item">
                          <IconButton size="small" color="error" onClick={() => handleDelete(item)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Button variant="contained" color="primary" href="/inventory/add" sx={{ mt: 2 }}>Add New Item</Button>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Inventory Item</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete {deleteItem?.itemName}? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleteLoading}>
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Drawer */}
      <Drawer
        title="Edit Inventory Item"
        placement="right"
        open={editOpen}
        onClose={() => setEditOpen(false)}
        width={420}
        destroyOnClose
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          {editError && <Alert severity="error">{editError}</Alert>}
          {editSuccess && <Alert severity="success">{editSuccess}</Alert>}
          {editItem && (
            <>
              {/* isMaterial Checkbox positioned at the very top of the editing field list */}
              <FormControlLabel
                control={
                  <Checkbox
                    name="isMaterial"
                    checked={editItem.isMaterial === 'Y'}
                    onChange={handleCheckboxChange}
                    color="primary"
                  />
                }
                label="Material"
              />

              {/* Shared Fields */}
              <TextField label="Item Name" name="itemName" value={editItem.itemName || ''} onChange={handleEditChange} required fullWidth />
              <TextField label="Description" name="itemDescription" value={editItem.itemDescription || ''} onChange={handleEditChange} required fullWidth />
              <TextField label="Category" name="itemCategory" value={editItem.itemCategory || ''} onChange={handleEditChange} required fullWidth />
              <TextField label="Item Number" name="itemNumber" value={editItem.itemNumber || ''} onChange={handleEditChange} required fullWidth />
              <TextField label="Price" name="price" value={editItem.price || ''} onChange={handleEditChange} required fullWidth type="number" />
              <TextField label="Purchase Price" name="purchasePrice" value={editItem.purchasePrice || ''} onChange={handleEditChange} fullWidth type="number" />

              {/* Conditional Material fields hidden when material === 'N' */}
              {editItem.isMaterial === 'Y' && (
                <>
                  <TextField label="SKU" name="sku" value={editItem.sku || ''} onChange={handleEditChange} required fullWidth />
                  <TextField label="Barcode" name="barcode" value={editItem.barcode || ''} onChange={handleEditChange} required fullWidth />
                  <TextField label="QR Code" name="qrCode" value={editItem.qrCode || ''} onChange={handleEditChange} required fullWidth />
                  <TextField label="Bin Code" name="binCode" value={editItem.binCode || ''} onChange={handleEditChange} required fullWidth />
                  <TextField label="Quantity" name="quantity" value={editItem.quantity || ''} onChange={handleEditChange} required fullWidth type="number" />
                  <TextField label="Supplier Name" name="supplierName" value={editItem.supplierName || ''} onChange={handleEditChange} required fullWidth />
                </>
              )}

              <TextField label="Note" name="note" value={editItem.note || ''} onChange={handleEditChange} fullWidth />
              
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button onClick={() => setEditOpen(false)} color="secondary" variant="outlined">Cancel</Button>
                <Button onClick={handleEditSave} variant="contained" color="primary" disabled={editLoading}>{editLoading ? 'Saving...' : 'Save'}</Button>
              </Stack>
            </>
          )}
        </Stack>
      </Drawer>
    </Paper>
  );
};

export default InventoryManagement;